import React, { useEffect } from "react";

import { INetskraflProps } from "../netskrafl/Netskrafl.types"; // Must be relative for Rollup to find the file
import { GlobalState } from "@/mithril/globalstate";
import { makeGlobalState } from "@/mithril/authSettings";
import { main } from "@/mithril/pageGataDagsins";

// Note: To load a specific date for debugging, use URL parameter: ?date=YYYY-MM-DD
// Example: http://localhost:6006/?date=2025-01-25

const mountForUser = async (state: GlobalState): Promise<HTMLDivElement> => {
  // Return a DOM tree containing a mounted Gáta Dagsins UI
  // for the user specified in the state object
  const { userEmail } = state;
  if (!userEmail) {
    // console.error("No user specified for Gáta Dagsins UI");
    throw new Error("No user specified for Gáta Dagsins UI");
  }
  // Check whether we already have a mounted UI for this user
  const elemId = `gatadagsins-user-${userEmail}`;
  const existing = document.getElementById(elemId);
  if (existing) {
    // console.log("Gáta Dagsins UI already mounted for user", userEmail);
    return existing as HTMLDivElement;
  }
  // Create a new div element to hold the UI
  const root = document.createElement('div');
  root.id = elemId;
  root.className = "gatadagsins-loading";
  // Attach the partially-mounted div to the document body
  // as a placeholder while the UI is being mounted
  document.body.appendChild(root);
  const loginResult = await main(state, root);
  if (loginResult === "success") {
    // The UI was successfully mounted
    root.className = "gatadagsins-user";
    return root;
  }
  // console.error("Failed to mount Gáta Dagsins UI for user", userEmail);
  throw new Error("Failed to mount Gáta Dagsins UI");
}

const GataDagsinsImpl = ({ state, tokenExpired }: INetskraflProps) => {
  const ref = React.createRef<HTMLDivElement>();
  const completeState = makeGlobalState({ ...state, tokenExpired });
  const { userEmail } = completeState;

  useEffect(() => {
    // Load the Gáta Dagsins (Mithril) UI for a new user
    if (!userEmail) return;
    const container = ref.current;
    if (!container) {
      console.error("No container for Gáta Dagsins UI");
      return;
    }
    const elemId = `gatadagsins-user-${userEmail}`;
    if (container.firstElementChild?.id === elemId) {
      // The Gáta Dagsins UI is already correctly mounted
      return;
    }
    try {
      mountForUser(completeState).then((div) => {
        // Attach the div as a child of the container
        // instead of any previous children
        const container = ref.current;
        if (container) {
          container.innerHTML = "";
          container.appendChild(div);
        }
      });
    } catch (err) {
      console.error("Failed to mount Gáta Dagsins UI for user", userEmail);
      const container = document.getElementById("gatadagsins-container");
      if (container)
        container.innerHTML = "";
    }
    return () => {
      // Move the Gáta Dagsins UI to a hidden div under the body element
      // when the component is unmounted
      // console.log("Dismounting Gáta Dagsins UI for user", userEmail);
      const container = document.getElementById("gatadagsins-container");
      const div = container?.firstElementChild;
      if (div?.id === elemId) {
        document.body.appendChild(div);
      }
    };
  }, [userEmail]);

  return (
    <div className="gatadagsins">
      <div className="netskrafl-container" id="gatadagsins-container" ref={ref}>
        { /* The 'Gáta dagsins' UI will be rendered here */ }
      </div>
    </div>
  );
};

export const GataDagsins = React.memo(GataDagsinsImpl);
